import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, Loader2, RotateCw } from 'lucide-react';
import AgentPlayground from './AgentPlayground';
import { AgentProps } from '../Interfaces/AgentProps';
import { ingestionService } from '../../../../../components/api/ingestionService';
import { agentBuilderService } from './agentBuilderService';
import { useParams } from 'react-router-dom';
import ConfirmDialog from '../../../Dialogs/ConfirmDialog';
import ReactMarkdown from 'react-markdown';

type Props = {
  agent?: AgentProps;
};
const initialWelcomeMessage = {
  role: 'bot',
  content: `👋 **Welcome to the Agent Builder!**  
I'm here to help you generate powerful agents tailored to your needs. Just describe what you'd like your agent to do, and I’ll handle the rest. Here's how it works:

 

🔧 **Step 1: Master Prompt**  
Your agent’s *main behavior* will be guided by its **Master Prompt**. This defines its core responsibility.

📦 **Step 2: Helper Resources**  
I’ll also look into any **helper resources** (like datasets, workspaces, or policies) you've linked. These will be used *if needed* to enrich or validate your agent’s logic.

🧠 **Step 3: Code Generation**  
Once your intent is clear, I’ll generate complete Python code for your agent — visible on the right in the code playground.

💾 **Step 4: Save & Deploy**  
Review the logic, test if needed, then save your version with a comment. It’ll be added to your version history!




✨ *Let’s begin — just type what your agent should do!*`,
};


const AgentBuilder: React.FC<Props> = ({ agent }) => {
  const [llmsList, setLlmsList] = useState<{ id: string; modelName: string }[]>([]);
  const [isFullScreen, setIsFullScreen] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullScreen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const [messages, setMessages] = useState([
    {
      role: 'bot',
      content:
        '👋 Hello! I’m your Agent Builder assistant. Tell me what you want your agent to do, and I’ll generate the logic for you.',
    },
  ]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedLLM, setSelectedLLM] = useState('gpt-4o-mini');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const tenantId = '3fa85f64-5717-4562-b3fc-2c963f66afa6';
  const [sessionId, setSessionId] = useState<string | null>(null);
  const { agentId } = useParams<{ agentId: string }>();
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [agentCode, setAgentCode] = useState<string>('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<'Backend' | 'FrontEnd'>('Backend');

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  useEffect(() => {
    ingestionService.getLLMConfigurations(tenantId).then((res) => {
      const models = res
        .filter((llm: any) => llm.isActive)
        .map((llm: any) => ({
          id: llm.id,
          modelName: llm.modelName || llm.additionalConfigJson?.deployment || 'Unnamed',
        }));
      setLlmsList(models);
      if (models.length > 0) {
        setSelectedLLM(models[0].id); // default select first
      }
    });
  }, []);

  const handleSend = async () => {
    if (!input.trim() || !sessionId || !agentId || !selectedLLM) return;

    const userMessage = { role: 'user', content: input.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Call backend
      setCodeLoading(true);
      const response = await agentBuilderService.sendMessage({
        sessionId,
        userId: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
        userMessage: userMessage.content,
        llmTypeId: selectedLLM,
      });

      const content: string = response.systemResponse?.content || '';
      if (response) {
        setCodeLoading(false);
      }
      // Extract <summary> content
      const summaryMatch = content.match(/<summary>([\s\S]*?)<\/summary>/i);
      const summary = summaryMatch ? summaryMatch[1].trim() : '✅ Response received.';

      const codeMatch = content.match(/<code>([\s\S]*?)<\/code>/i);
      const extractedCode = codeMatch ? codeMatch[1].trim() : '';
      if (extractedCode) setAgentCode(extractedCode); // push to playground


      // Append bot response
      const botMessage = { role: 'bot', content: summary };
      setMessages((prev) => [...prev, botMessage]);
    } catch (err) {
      console.error('Message send failed:', err);
      setMessages((prev) => [
        ...prev,
        { role: 'bot', content: '⚠️ Failed to get a response. Please try again.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const reloadAgentData = async (builderType: number) => {
    if (!sessionId || !agentId) return;

    try {
      // Reload chat messages
      const messages = await agentBuilderService.getAgentChatMessages(sessionId, agentId);
      const parsedMessages = messages.map((msg: any) => {
        if (msg.role === 0) {
          return { role: 'user', content: msg.content };
        } else {
          const summaryMatch = msg.content.match(/<summary>([\s\S]*?)<\/summary>/i);
          const summary = summaryMatch ? summaryMatch[1].trim() : '✅ Response received.';
          return { role: 'bot', content: summary };
        }
      });
      setMessages(parsedMessages.length > 0 ? parsedMessages : [initialWelcomeMessage]);

      // Reload source code with builderType
      const builderType = selectedType === 'Backend' ? 1 : 2;
      const source = await agentBuilderService.getAgentSourceCode(agentId, builderType);
      if (source?.sourceCode) {
        const codeMatch = source.sourceCode.match(/<code>([\s\S]*?)<\/code>/i);
        const extractedCode = codeMatch ? codeMatch[1].trim() : '';
        setAgentCode(extractedCode);
      } else {
        setAgentCode('');
      }
    } catch (err) {
      console.error('Failed to reload agent data:', err);
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [input]);
  useEffect(() => {
    const init = async () => {
      const session = await agentBuilderService.startOrGetSession(
        agentId || 'default-agent-id',
        'd0899e06-d0b4-4167-a8dd-a8154a1e9f38',
        'Agent Builder'
      );
      setSessionId(session.sessionId);

      if (session.sessionId && agentId) {
        try {
          const messages = await agentBuilderService.getAgentChatMessages(session.sessionId, agentId);

          const parsedMessages = messages.map((msg: any) => {
            if (msg.role === 0) {
              // User message
              return {
                role: 'user',
                content: msg.content,
              };
            } else {
              // LLM/system response
              const summaryMatch = msg.content.match(/<summary>([\s\S]*?)<\/summary>/i);
              const summary = summaryMatch ? summaryMatch[1].trim() : '✅ Response received.';
              return {
                role: 'bot',
                content: summary,
              };
            }
          });

          setMessages(parsedMessages.length > 0 ? parsedMessages : [initialWelcomeMessage]);


          const source = await agentBuilderService.getAgentSourceCode(agentId);
          if (source?.sourceCode) {
            const codeMatch = source.sourceCode.match(/<code>([\s\S]*?)<\/code>/i);
            const extractedCode = codeMatch ? codeMatch[1].trim() : '';
            setAgentCode(extractedCode);
          }
        } catch (err) {
          console.error('Failed to load chat history:', err);
        }
      }
    };

    init();
  }, []);


  const resetSession = async () => {
    const session = await agentBuilderService.startNewSession(
      agentId ?? '',
      'd0899e06-d0b4-4167-a8dd-a8154a1e9f38',
      'Agent Builder'
    );
    setSessionId(session.sessionId);
    setMessages([initialWelcomeMessage]);
    setInput('');
    setAgentCode("");
  };


  return (
    <div
      className={`${isFullScreen
        ? 'fixed inset-0 z-50 w-screen h-[100vh] bg-neutral-950'
        : 'h-[calc(100vh-4rem)]'
        } flex flex-col transition-all duration-300`}
    >
      <div className="h-[calc(100vh-4rem)] flex flex-col bg-neutral-950">
        {/* <div className="flex items-center gap-2 px-6 py-3 border-b border-neutral-800">
          <Bot className="w-4 h-4 text-indigo-500" />
          <h1 className="text-xs font-medium text-white">
            Agent Builder {sessionId ? ` - ${sessionId}` : ''}</h1>
          <button
            onClick={() => setIsFullScreen((prev) => !prev)}
            className="ml-auto text-indigo-400 hover:text-white transition p-1"
            title={isFullScreen ? 'Exit Fullscreen' : 'Maximize'}
            style={{ border: 'none', background: 'transparent' }}
          >
            {isFullScreen ? '⤢' : '↗'}
          </button>

        </div> */}

        <div className="flex items-center gap-2 px-6 py-3 border-b border-neutral-800">
          <Bot className="w-4 h-4 text-indigo-500" />
          <h1 className="text-xs font-medium text-white">
            Agent Builder {sessionId ? ` - ${sessionId}` : ''}
          </h1>

          {/* New Dropdown */}
          {/* <div className="ml-4">
            <select
              value={selectedType}
              onChange={async (e) => {
                const newType = e.target.value as 'Backend' | 'FrontEnd';
                setSelectedType(newType);
                const builderType = newType === 'Backend' ? 1 : 2;
                await reloadAgentData(builderType);
              }}
              className="text-xs bg-neutral-800 text-white px-3 py-1 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 border border-neutral-700"
            >
              <option value="Backend">Backend</option>
              <option value="FrontEnd">FrontEnd</option>
            </select>
          </div> */}

          <button
            onClick={() => setIsFullScreen((prev) => !prev)}
            className="ml-auto text-indigo-400 hover:text-white transition p-1"
            title={isFullScreen ? 'Exit Fullscreen' : 'Maximize'}
            style={{ border: 'none', background: 'transparent' }}
          >
            {isFullScreen ? '⤢' : '↗'}
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel */}
          <div className="w-full md:w-1/3 h-full flex flex-col border-r border-neutral-800 bg-neutral-950">

            <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs text-white">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-2 ${msg.role === 'user' ? 'justify-end' : ''
                    }`}
                >
                  {msg.role === 'bot' && (
                    <Bot className="w-4 h-4 text-indigo-400 mt-1" />
                  )}
                  <div
                    className={`max-w-xs px-3 py-2 rounded-md ${msg.role === 'bot'
                      ? 'bg-neutral-800 text-left'
                      : 'bg-indigo-600 text-right'
                      }`}
                  >
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="animate-pulse border border-indigo-500 rounded-md px-4 py-3 bg-neutral-800 text-xs text-indigo-300 shadow-md w-full max-w-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
                      <span>Generating code for you... please wait</span>
                    </div>
                    <div className="mt-2 flex gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce"></div>
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce delay-150"></div>
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce delay-300"></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />

            </div>

            <div className="px-4 pt-2 pb-3 border-t border-neutral-800 bg-neutral-900 space-y-2">
              {/* Input Area */}
              <div className="flex items-start gap-2">
                <textarea
                  ref={inputRef}
                  rows={3}
                  placeholder="Describe your agent’s behavior..."
                  className="flex-1 resize-none px-3 py-2 rounded-md bg-neutral-800 text-white text-xs focus:outline-none max-h-[10.5rem] overflow-auto leading-relaxed"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = '120px'; // Reset height
                    target.style.height = `${target.scrollHeight}px`; // Adjust to content
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                />

                <button
                  onClick={handleSend}
                  className="p-2 mt-1 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>

              {/* Footer: LLM & Reset */}
              <div className="flex items-center justify-between text-xs text-white gap-4">
                <div className="flex items-center gap-2">
                  <select
                    id="llm"
                    value={selectedLLM}
                    onChange={(e) => setSelectedLLM(e.target.value)}
                    className="text-xs bg-neutral-800 text-white px-2 py-1 rounded-md focus:outline-none"
                  >
                    {llmsList.map((llm) => (
                      <option key={llm.id} value={llm.id}>
                        {llm.modelName}
                      </option>
                    ))}
                  </select>

                </div>

                <button
                  title="Reset Session"
                  className="flex items-center gap-1 text-gray-400 hover:text-white transition text-xs"
                  onClick={() => setShowConfirmReset(true)}
                >
                  <RotateCw className="w-4 h-4" />
                  Reset
                </button>
              </div>
              <div></div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="flex-1 bg-neutral-900 p-6 border-l border-neutral-800 overflow-auto">
            <AgentPlayground codeLoading={codeLoading} codeFromChat={agentCode} />
          </div>
        </div>
        {showConfirmReset && (
          <ConfirmDialog
            message="This will reset the current agent context and start a new session. Continue?"
            onYes={async () => {
              await resetSession();
              setShowConfirmReset(false);
            }}
            onCancel={() => setShowConfirmReset(false)}
          />
        )}

      </div>
    </div>
  );
};

export default AgentBuilder;
